import React, { useState, useEffect, useCallback } from 'react';
import { TopMenu } from './TopMenu/TopMenu.component';
import { Footer } from './Footer/Footer.component';
import { AssessmentList } from './AssessmentList/AssessmentList.component';
import { ScoresList } from './ScoresList.component';
import { GradeRules } from './TopMenu/GradeRules.component';
import { SwitchCardsView } from './TopMenu/SwitchCardsView.component';
import { Header } from './TopMenu/Header.component';
import { Summary } from './Footer/Summary.component';
import { Progress } from './Footer/Progress.component';
import { ExamTask, ExamScoringSchema } from '../../models/assessment.types';
import { ExamTaskGroupSolutionsNavigation } from './../../pages/Assessment/Assessment.types';
import { useStyles } from './AssessmentPanel.styles';
import { useHistory } from 'react-router-dom';
import {
  getAssessmentSampleAssessmentGroupSolutionUrl,
  getAssessmentUrl,
  getSampleAssessmentGroupSolutionAssignmentUrl,
  getSampleAssessmentRepositoryUrl,
  getSampleAssessmentUrl,
  getSolutionUrl,
} from '../../utils/routerUrls';
import { Prompt } from '../Prompt/Prompt.component';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import { AssessmentSessionProcessTypeId, Status } from '../../services/exams/assessmentSession/assessments.types';
import { TaskTypeFilterValue } from './TopMenu/TaskTypeDropdown.component';
import { SolutionViewMode } from '../../pages/Solution/Solution.component';
import { ClosedStatusFooter } from './Footer/ClosedStatus/ClosedStatusFooter.component';
import { getAssessmentSessionProcessLimitToleranceRange } from '../../services/exams/assessmentSession/assessments.service';
import { AxiosResponse } from 'axios';
import {
  ToleranceRangeResponse,
  ToleranceRangeTaskGroup,
} from '../../pages/Sessions/SessionsParametersDialog/ToleranceRange/ToleranceRange.types';
import { AnnoAndCommVisibilityManager } from './AnnotationsAndCommentsVisibilityManager/AnnoAndCommVisibilityManager.component';
import { SampleAssessmentSessionState } from '../../pages/Dashboard/Dashboard.types';
import { useScoring } from '../../Context/Scoring.context';
import { AnnotationOnImage } from '../../models/annotation.types';
import { useLayoutContext } from '../../Context/Layout.context';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { ExamTaskGroupSolution } from '../../services/exams/assessmentSession/assessments.types';

export type ViewType = 'cards' | 'solutions';

export interface AssessmentPanelProps {
  mode: SolutionViewMode;
  fileFullNameExamSheet: string | null;
  examTaskGroupSolutionsNavigation: ExamTaskGroupSolutionsNavigation;
  notAssessedExamTaskGroupSolutionsNavigation: ExamTaskGroupSolutionsNavigation;
  examinerFunction: ExaminerFunction;
  isSpecialCaseReported: boolean;
  chosenViewType: (view: ViewType) => void;
  schoolId?: string;
  examinerId?: string;
  rgzId?: string;
  orgzId?: string;
  sampleAssessmentSelectedProcess?: AssessmentSessionProcessTypeId | null;
  sampleAssessmentSessionStateId?: SampleAssessmentSessionState;
  onSendbackOrConfirmCompleted?: () => void;
  annotationsVisible: boolean;
  annotationsVisibilityChanged: (newValue: boolean) => void;
  commentsOnPageVisible: boolean;
  commentsOnPageVisibilityChanged: (newValue: boolean) => void;
  allCommentsOnPageVisible: boolean;
  allCommentsOnPageVisibilityChanged: (newValue: boolean) => void;
  checkIfUserCanEditScore: () => boolean;
  gradingAnnotationAddedToCriterion: AnnotationOnImage[] | undefined;
  anyAnnotationForGradingProvided: boolean;
  isFullscreen: boolean;
  isAnyModalOpened: boolean;
  examTaskGroupSolutionsList: ExamTaskGroupSolution[];
  onCommentChanged: () => void;
  keyboardShortcutsDisabled: boolean;
  isSkipLink: boolean;
}

export const AssessmentPanel: React.FC<AssessmentPanelProps> = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const {
    examTaskGroupSolutionWithTasks,
    activeTask,
    activeCriterion,
    totalScore,
    maxTotalScore,
    progress,
    examTaskGroupSolutionScoreVersionId,
    assessmentSessionId,
    processTypeId: processTypeIdStr,
    examinerId,
    deskId,
    orgzId,
    examinerFunction,
    isTabView,
    gradingInfoModalOpen,
    taskTypeFilter,
    activeCriterionIndex,
    setActiveTask,
    activateCriterion,
    setTaskTypeFilter,
    setExamTaskGroupSolutionScoreVersionId,
    setGradingInfoModalOpen,
    setActiveCriterionIndex,
  } = useScoring();
  const { openAlert } = useLayoutContext();

  const assessmentSession = useSelector((state: RootState) => state.assessmentSession.assessmentSession);

  if (!examTaskGroupSolutionWithTasks) {
    throw new Error('empty tasks list');
  }

  const processTypeId: AssessmentSessionProcessTypeId = parseInt(
    processTypeIdStr || '',
  ) as AssessmentSessionProcessTypeId;
  const [viewType, setViewType] = useState<ViewType>('cards');
  const [activeTaskIndexFromFiltered, setActiveTaskIndexFromFiltered] = useState<number>(-1);
  const [activeTaskIndexFromAll, setActiveTaskIndexFromAll] = useState<number>(-1);
  const [allTasks, setAllTasks] = useState<ExamTask[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<ExamTask[]>([]);
  const [toleranceRanges, setToleranceRanges] = useState<ToleranceRangeTaskGroup | undefined>(undefined);
  const [isCriterionSelected, setIsCriterionSelected] = useState<boolean>(false);

  const setActiveCriterionSet = (criterion: ExamScoringSchema | null, examTask: ExamTask) => {
    activateCriterion(criterion);
    if (criterion) {
      if (examTask)
        setActiveCriterionIndex(
          examTask.examScoringSchemas.findIndex(
            (item: ExamScoringSchema) => item.examScoringSchemaId === criterion.examScoringSchemaId,
          ),
        );
    } else {
      setActiveCriterionIndex(-1);
      setIsCriterionSelected(false);
    }
  };

  const setActiveTaskSet = (examTask?: ExamTask) => {
    if (examTask) {
      setActiveTask(examTask);
      setActiveTaskIndexFromFiltered(
        filteredTasks.findIndex((item: ExamTask) => item.taskNumber === examTask.taskNumber),
      );
      setActiveTaskIndexFromAll(allTasks.findIndex((item: ExamTask) => item.taskNumber === examTask.taskNumber));
      setActiveTask(examTask);
      setActiveTaskIndexFromAll(
        examTask ? allTasks.findIndex((item: ExamTask) => item.taskNumber === examTask.taskNumber) : -1,
      );
      setActiveTask(examTask || null);
    }
  };

  const handleSwitchView = (view: ViewType) => {
    setViewType(view);
    props.chosenViewType(view);
  };

  const filterAndUpdateNotAssessedTasks = () => {
    const notAssesedTasks = allTasks.filter((task: ExamTask) => {
      const notAssesed =
        (task.noAnswer === null || task.noAnswer === false) &&
        task.examScoringSchemas.findIndex((value: ExamScoringSchema) => value.score === null) > -1;
      return notAssesed;
    });
    let newFilteredTasks: ExamTask[] = [];

    if (viewType === 'cards') {
      newFilteredTasks = notAssesedTasks;
    } else {
      if (!activeTask) {
        newFilteredTasks = [notAssesedTasks[0]];
      } else {
        const task = notAssesedTasks.find((item: ExamTask) => item.taskNumber === activeTask?.taskNumber);
        newFilteredTasks = task ? [task] : [];
      }
    }
    setFilteredTasks(newFilteredTasks);
  };

  useEffect(() => {
    if (filteredTasks.length)
      if (activeTask) {
        setActiveTaskSet(
          filteredTasks.find((item: ExamTask) => {
            return item.taskNumber === activeTask?.taskNumber;
          }) || filteredTasks.at(0),
        );
      } else {
        setActiveTaskSet(filteredTasks.at(0));
      }
  }, [filteredTasks, activeTask]);

  useEffect(() => {
    if (examTaskGroupSolutionWithTasks) {
      setAllTasks(examTaskGroupSolutionWithTasks.examTasksCriteria);
    }
  }, [examTaskGroupSolutionWithTasks.examTasksCriteria]);

  useEffect(() => {
    if (taskTypeFilter === 'notAssessed') {
      filterAndUpdateNotAssessedTasks();
    } else {
      if (viewType === 'cards') {
        setFilteredTasks(allTasks);
      } else {
        if (!activeTask) {
          setFilteredTasks([allTasks[0]]);
        } else {
          const task = allTasks.find((item: ExamTask) => item.taskNumber === activeTask?.taskNumber);
          setFilteredTasks(task ? [task] : []);
        }
      }
    }
  }, [allTasks, viewType, taskTypeFilter, activeCriterion, activeTask?.taskId]);

  const getSolutionNavigationUrl = (navigationId: string): string => {
    switch (props.mode) {
      case SolutionViewMode.assessment:
        return getSolutionUrl(navigationId, examinerId, processTypeIdStr, deskId, examinerFunction);
      case SolutionViewMode.assessmentSampleAssessmentGroup:
        const orgzIdToNavigate = props.examTaskGroupSolutionsList.find((element) => element.id === navigationId);
        return getAssessmentSampleAssessmentGroupSolutionUrl(
          props.schoolId,
          navigationId,
          orgzIdToNavigate?.examTaskGroupSolutionScoreId,
          props.examinerId,
          examinerFunction,
        );
      case SolutionViewMode.sampleAssessmentGroupAssignment:
        return getSampleAssessmentGroupSolutionAssignmentUrl(props.schoolId, navigationId, props.examinerId);
    }
  };

  const getSourceTableNavigationUrl = (): string => {
    switch (props.mode) {
      case SolutionViewMode.assessment:
        return getAssessmentUrl(assessmentSessionId, processTypeIdStr, examinerId);
      case SolutionViewMode.assessmentSampleAssessmentGroup:
        return getSampleAssessmentUrl(assessmentSession?.assessmentSessionId, examinerId);
      case SolutionViewMode.sampleAssessmentGroupAssignment:
        return getSampleAssessmentRepositoryUrl();
    }
  };

  const openGradingInfoModal = () => {
    setGradingInfoModalOpen(true);
  };

  const closeGradingInfoModal = () => {
    setGradingInfoModalOpen(false);
  };

  const confirmGradingInfoModal = () => {
    setGradingInfoModalOpen(false);
    history.push(getSourceTableNavigationUrl());
  };

  const handleOpenTask = useCallback(
    (taskNumber: string) => {
      const nextTask = filteredTasks.find((task: ExamTask) => task.taskNumber === taskNumber);
      if (nextTask) {
        setActiveTaskSet(nextTask);
        setActiveCriterionSet(null, nextTask);
      }
    },
    [filteredTasks],
  );
  const handleOpenCriterion = useCallback(
    (taskNumber: string, examScoringSchemaId: string) => {
      const nextTask = filteredTasks.find((task: ExamTask) => task.taskNumber === taskNumber);
      setActiveTaskSet(nextTask);
      if (nextTask) {
        const criterion = nextTask.examScoringSchemas.find(
          (criterion: ExamScoringSchema) => criterion.examScoringSchemaId === examScoringSchemaId,
        );
        if (criterion && nextTask) {
          setActiveCriterionSet(criterion, nextTask);
          setIsCriterionSelected(true);
        }
      }
    },
    [filteredTasks],
  );

  const handleTaskChange = useCallback(
    (value: 'next' | 'previous') => {
      if (!activeTask) return;

      const examTaskGroupSolutionsNavigation =
        taskTypeFilter === 'notAssessed'
          ? props.notAssessedExamTaskGroupSolutionsNavigation
          : props.examTaskGroupSolutionsNavigation;

      switch (value) {
        case 'next':
          {
            if (filteredTasks.length > 1 && activeTaskIndexFromFiltered < filteredTasks.length - 1) {
              const nextTask = filteredTasks.at(activeTaskIndexFromFiltered + 1);
              if (nextTask) {
                setActiveTaskSet(nextTask);
                setActiveCriterionSet(null, nextTask);
              }
            } else if (
              viewType === 'solutions' &&
              examTaskGroupSolutionsNavigation.nextId === null &&
              examTaskGroupSolutionsNavigation.firstId !== null &&
              activeTaskIndexFromAll < allTasks.length - 1
            ) {
              history.push(getSolutionNavigationUrl(examTaskGroupSolutionsNavigation.firstId));
              const nextTask = allTasks[activeTaskIndexFromAll + 1];
              setFilteredTasks([nextTask]);
              setActiveTaskSet(nextTask);
              setActiveCriterionSet(null, nextTask);
            } else if (examTaskGroupSolutionsNavigation.nextId !== null) {
              history.push(getSolutionNavigationUrl(examTaskGroupSolutionsNavigation.nextId));
              const nextTask = filteredTasks.at(0);
              if (nextTask) {
                setActiveTaskSet(nextTask);
                setActiveCriterionSet(null, nextTask);
              }
            } else {
              openGradingInfoModal();
            }
          }
          break;
        case 'previous':
          {
            if (filteredTasks.length > 1 && activeTaskIndexFromFiltered > 0) {
              const prevTask = filteredTasks.at(activeTaskIndexFromFiltered - 1);
              if (prevTask) {
                setActiveTaskSet(prevTask);
                setActiveCriterionSet(null, prevTask);
              }
            } else if (
              viewType === 'solutions' &&
              examTaskGroupSolutionsNavigation.prevId === null &&
              examTaskGroupSolutionsNavigation.lastId !== null &&
              activeTaskIndexFromAll > 0
            ) {
              history.push(getSolutionNavigationUrl(examTaskGroupSolutionsNavigation.lastId));
              const prevTask = allTasks[activeTaskIndexFromAll - 1];
              setFilteredTasks([prevTask]);
              setActiveTaskSet(prevTask);
              setActiveCriterionSet(null, prevTask);
            } else if (examTaskGroupSolutionsNavigation.prevId !== null) {
              history.push(getSolutionNavigationUrl(examTaskGroupSolutionsNavigation.prevId));
              if (filteredTasks.length > 0) {
                handleOpenTask(filteredTasks[filteredTasks.length - 1].taskNumber);
                setActiveTask(filteredTasks[filteredTasks.length - 1]);
              }
            } else {
              openGradingInfoModal();
            }
          }
          break;
      }
    },
    [
      props.examTaskGroupSolutionsNavigation,
      props.notAssessedExamTaskGroupSolutionsNavigation,
      filteredTasks,
      activeTaskIndexFromFiltered,
      viewType,
    ],
  );

  const handleCriterionChange = useCallback(
    (value: 'next' | 'previous') => {
      if (!activeTask) return;

      switch (value) {
        case 'next':
          {
            if (
              activeTaskIndexFromFiltered === filteredTasks.length - 1 &&
              activeCriterionIndex === activeTask.examScoringSchemas.length - 1
            )
              return;
            if (activeCriterionIndex < activeTask.examScoringSchemas.length - 1) {
              const nextCriterion = activeTask.examScoringSchemas.at(activeCriterionIndex + 1);
              if (nextCriterion) {
                setActiveCriterionSet(nextCriterion, activeTask);
                setIsCriterionSelected(true);
              }
            } else {
              const nextTask = filteredTasks.at(activeTaskIndexFromFiltered + 1);
              if (nextTask) {
                setActiveTaskSet(nextTask);
                const nextCriterion = nextTask.examScoringSchemas[0];
                setActiveCriterionSet(nextCriterion, nextTask);
                setIsCriterionSelected(true);
              }
            }
          }
          break;
        case 'previous': {
          if (activeTaskIndexFromFiltered === 0 && activeCriterionIndex === 0) return;
          if (activeCriterionIndex > 0) {
            const prevCriterion = activeTask.examScoringSchemas.at(activeCriterionIndex - 1);
            if (prevCriterion) {
              setActiveCriterionSet(prevCriterion, activeTask);
              setIsCriterionSelected(true);
            }
          } else {
            const prevTask = filteredTasks.at(activeTaskIndexFromFiltered - 1);
            if (prevTask) {
              setActiveTaskSet(prevTask);
              const prevCriterion = prevTask.examScoringSchemas[prevTask.examScoringSchemas.length - 1];
              setActiveCriterionSet(prevCriterion, prevTask);
              setIsCriterionSelected(true);
            }
          }
        }
      }
    },
    [activeTaskIndexFromFiltered, activeCriterionIndex, activeTask?.taskId, filteredTasks],
  );

  useEffect(() => {
    if (activeTask && !isCriterionSelected) {
      handleOpenTask(activeTask.taskNumber);
    }
  }, [orgzId, activeTask?.taskId, isCriterionSelected]);

  useEffect(() => {
    if (
      processTypeId === AssessmentSessionProcessTypeId.Qualification ||
      (processTypeId === AssessmentSessionProcessTypeId.Scoring &&
        [ExaminerFunction.PZE, ExaminerFunction.KGZ, ExaminerFunction.KK].includes(props.examinerFunction))
    ) {
      getAssessmentSessionProcessLimitToleranceRange(
        processTypeId === AssessmentSessionProcessTypeId.Qualification
          ? AssessmentSessionProcessTypeId.Qualification.toString()
          : AssessmentSessionProcessTypeId.Control.toString(),
      ).then(
        (response: AxiosResponse<ToleranceRangeResponse>) => {
          setToleranceRanges(
            Array.isArray(response.data.examTaskGroup) ? response.data.examTaskGroup[0] : response.data.examTaskGroup,
          );
        },
        () => openAlert('error'),
      );
    }
  }, [processTypeId]);

  const isAssessmentMode: boolean =
    props.mode === SolutionViewMode.assessment || props.mode === SolutionViewMode.assessmentSampleAssessmentGroup;

  const canEditScore = props.checkIfUserCanEditScore();

  if (!activeTask || examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreState === null) {
    return null;
  } else
    return (
      <>
        <div className={classes.container}>
          <TopMenu>
            {activeTask && isAssessmentMode && (
              <GradeRules gradeRulesId={activeTask.gradeRule.id} fileName={props.fileFullNameExamSheet} />
            )}
            {isAssessmentMode && <SwitchCardsView handleSwitchView={handleSwitchView} viewType={viewType} />}
          </TopMenu>
          {props.mode !== SolutionViewMode.sampleAssessmentGroupAssignment && (
            <AnnoAndCommVisibilityManager
              annotationsVisible={props.annotationsVisible}
              annotationsVisibilityChanged={props.annotationsVisibilityChanged}
              commentsOnPageVisible={props.commentsOnPageVisible}
              commentsOnPageVisibilityChanged={props.commentsOnPageVisibilityChanged}
              allCommentsOnPageVisible={props.allCommentsOnPageVisible}
              allCommentsOnPageVisibilityChanged={props.allCommentsOnPageVisibilityChanged}
            />
          )}
          <Header
            taskTypeFilter={taskTypeFilter}
            onTaskTypeSelect={setTaskTypeFilter}
            showTaskTypeDropdown={props.mode === SolutionViewMode.assessment}
          />
          <div className={classes.assesmentContainer}>
            <AssessmentList
              mode={props.mode}
              examTasks={filteredTasks}
              activeTaskIndex={activeTaskIndexFromFiltered}
              activeCriterionIndex={activeCriterionIndex}
              activeTaskNo={activeTask.taskNumber}
              onOpenTask={handleOpenTask}
              onOpenCriterion={handleOpenCriterion}
              onTaskChange={(value) => {
                if (taskTypeFilter === 'notAssessed') {
                  filterAndUpdateNotAssessedTasks();
                  handleTaskChange(value);
                } else handleTaskChange(value);
              }}
              onCriterionChange={handleCriterionChange}
              viewType={viewType}
              examTaskGroupSolutionsNavigation={props.examTaskGroupSolutionsNavigation}
              examinerFunction={props.examinerFunction}
              examTaskGroupSolutionScoreState={examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreState}
              examinerId={examinerId}
              canEditScore={canEditScore}
              processTypeId={processTypeId}
              tasksToleranceRanges={toleranceRanges ? toleranceRanges.task : []}
              orgzId={props.orgzId ?? ''}
              anyAnnotationForGradingProvided={props.anyAnnotationForGradingProvided}
              isFullscreen={props.isFullscreen}
              isAnyModalOpened={props.isAnyModalOpened}
              keyboardShortcutsDisabled={props.keyboardShortcutsDisabled}
              isSkipLink={props.isSkipLink}
            />
            <ScoresList
              isOpen={!!activeCriterion && canEditScore && !activeTask.noAnswer && filteredTasks.length > 0}
              disabled={activeCriterion?.isScoringAnnotationRequired && props.anyAnnotationForGradingProvided}
              gradingAnnotationAddedToCriterion={props.gradingAnnotationAddedToCriterion}
              onCommentChanged={props.onCommentChanged}
            />
          </div>
          {examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreState === Status.Closed &&
          examTaskGroupSolutionWithTasks.sampleAssessmentPoints !== null ? (
            <ClosedStatusFooter
              totalScore={totalScore}
              maxTotalScore={maxTotalScore}
              sampleAssessmentScoreSum={examTaskGroupSolutionWithTasks.sampleAssessmentPoints}
              assessmentQuality={examTaskGroupSolutionWithTasks.assessmentQuality}
              finalAssessmentQuality={examTaskGroupSolutionWithTasks.finalAssessmentQuality}
              processTypeId={processTypeId}
              toleranceRange={toleranceRanges?.toleranceRange ?? null}
            />
          ) : (
            <Footer>
              <Summary mode={props.mode} totalScore={totalScore} maxTotalScore={maxTotalScore} />
              {isAssessmentMode && (
                <Progress
                  rgzId={props.rgzId}
                  orgzId={props.orgzId}
                  progress={progress}
                  examinerFunction={props.examinerFunction}
                  isSpecialCaseReported={props.isSpecialCaseReported}
                  examTaskGroupSolutionScoreState={examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreState}
                  examTaskGroupSolutionScoreId={examTaskGroupSolutionWithTasks.examTaskGroupSolutionScoreId}
                  sampleAssessmentSelectedProcess={props.sampleAssessmentSelectedProcess}
                  mode={props.mode}
                  onSendbackOrConfirmCompleted={props.onSendbackOrConfirmCompleted}
                  examTaskGroupSolutionScoreVersionId={examTaskGroupSolutionScoreVersionId}
                  onShelfSendBack={() => setExamTaskGroupSolutionScoreVersionId(undefined)}
                />
              )}
            </Footer>
          )}
        </div>
        <Prompt
          type="confirmation"
          open={gradingInfoModalOpen}
          size="xs"
          onCancel={!isTabView ? closeGradingInfoModal : undefined}
          onConfirm={confirmGradingInfoModal}
          mainActionButtonText="Powrót do listy"
          mainText={
            <>
              Oceniasz{' '}
              {props.examTaskGroupSolutionsNavigation.prevId === null ? <span>pierwsze</span> : <span>ostatnie</span>}{' '}
              zadanie z{' '}
              {props.examTaskGroupSolutionsNavigation.prevId === null ? <span>pierwszej</span> : <span>ostatniej</span>}{' '}
              <br /> pobranej karty rozwiązań
            </>
          }
        ></Prompt>
      </>
    );
};
